import { DateTimeInput } from "components/FormControls/DateTimeInput";
import { NumberFormatInput } from "components/FormControls/NumberFormatInput";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { findConditionLabel } from "components/Layout/Campaign/Common/CampaignRules/Rule/Conditions/utils";
import { format } from "date-fns";
import { useMessage } from "hooks/intl";
import { get } from "lodash";
import { NumberOfTransactionsInGivenPeriod as NumberOfTransactionsInGivenPeriodType } from "types/segments";
import { OLDate } from "utilities/dateFormat";
import { BoxElement } from "./BoxElement";
import { useStyles } from "./SegmentConditions.styles";
import { segmentConditions } from "./helpers";

export const initialValuesNumberOfTransactionsInGivenPeriod: NumberOfTransactionsInGivenPeriodType =
  {
    type: "transaction_count_in_period",
    operator: { code: "" },
    fromDate: null,
    toDate: null,
    count: 0,
  };

export function NumberOfTransactionsInGivenPeriod({
  formField,
  values,
  status,
  readOnly,
  setFieldValue,
}: {
  formField: string;
  values: any;
  status: any;
  readOnly?: boolean;
  setFieldValue: (field: string, value: any) => void;
}) {
  const classes = useStyles();
  const { message } = useMessage();

  const value = get(values, formField);
  const countError = get(status, "count");
  const fromCountError = get(status, "fromCount");
  const toCountError = get(status, "toCount");
  const fromDateError = get(status, "fromDate");
  const toDateError = get(status, "toDate");
  const isTransactionCountRange = value.operator.code === "is_number_between";
  const dataTestId = "number-of-transactions-in-given-period";

  return (
    <>
      {value.isConditionSaved || readOnly ? (
        <>
          {message({
            id: findConditionLabel(value.operator.code),
          }).toLocaleLowerCase()}{" "}
          {!isTransactionCountRange && (
            <BoxElement content={value?.count} error={countError} />
          )}
          {isTransactionCountRange && (
            <>
              {message({ id: "is_between" }).toLocaleLowerCase()}
              <BoxElement content={value?.fromCount} error={fromCountError} />
              {message({ id: "and" }).toLocaleLowerCase()}
              <BoxElement content={value?.toCount} error={toCountError} />
            </>
          )}
          {message({ id: "inPeriodFrom" }).toLocaleLowerCase()}
          <BoxElement content={OLDate(value?.fromDate)} error={fromDateError} />
          {message({ id: "to" }).toLocaleLowerCase()}
          <BoxElement content={OLDate(value?.toDate)} error={toDateError} />
        </>
      ) : (
        <>
          <SelectInput
            className={!isTransactionCountRange ? classes.input : ""}
            data-test-id={`${dataTestId}-operator`}
            items={segmentConditions.map(
              ({ id, label }: { id: string; label: string }) => ({
                id: id,
                label: message({ id: label }),
              }),
            )}
            label={<RequiredFieldLabel text={message({ id: "condition" })} />}
            name={`${formField}.operator.code`}
            onChange={(_, value) => {
              if (value === "is_number_between") {
                setFieldValue(`${formField}.count`, undefined);
                setFieldValue(`${formField}.fromCount`, 0);
                setFieldValue(`${formField}.toCount`, 0);
              } else {
                setFieldValue(`${formField}.count`, 0);
                setFieldValue(`${formField}.fromCount`, undefined);
                setFieldValue(`${formField}.toCount`, undefined);
              }
            }}
          />
          {!isTransactionCountRange && (
            <NumberFormatInput
              allowNegative={false}
              allowDecimals
              label={
                <RequiredFieldLabel
                  text={message({
                    id: "numberOfTransactions",
                  })}
                />
              }
              name={`${formField}.count`}
              data-test-id={`${dataTestId}-count`}
              className={classes.input}
            />
          )}
          {isTransactionCountRange && (
            <>
              <NumberFormatInput
                allowNegative={false}
                allowDecimals
                label={
                  <RequiredFieldLabel
                    text={message({
                      id: "numberOfTransactionsFrom",
                    })}
                  />
                }
                name={`${formField}.fromCount`}
                data-test-id={`${dataTestId}-transactions-from`}
                className={classes.input}
              />
              <NumberFormatInput
                allowNegative={false}
                allowDecimals
                label={
                  <RequiredFieldLabel
                    text={message({
                      id: "numberOfTransactionsTo",
                    })}
                  />
                }
                name={`${formField}.toCount`}
                data-test-id={`${dataTestId}-transaction-to`}
                className={classes.input}
              />
            </>
          )}
          <DateTimeInput
            className={classes.input}
            label={<RequiredFieldLabel text={message({ id: "dateFrom" })} />}
            name={`${formField}.fromDate`}
            data-test-id={`${dataTestId}-date-from`}
            onChange={(value) =>
              setFieldValue(
                `${formField}.fromDate`,
                format(new Date(value), "yyyy-MM-dd HH:mmXXX"),
              )
            }
          />
          <DateTimeInput
            className={classes.input}
            label={<RequiredFieldLabel text={message({ id: "dateTo" })} />}
            name={`${formField}.toDate`}
            data-test-id={`${dataTestId}-date-to`}
            onChange={(value) =>
              setFieldValue(
                `${formField}.toDate`,
                format(new Date(value), "yyyy-MM-dd HH:mmXXX"),
              )
            }
          />
        </>
      )}
    </>
  );
}
